import {
  CUSTOM_TIMEOFF_TYPE_IDENTIFIERS,
  isCustomTimeOffTypeIdentifier,
  isTimeOffTypeIdentifier,
  type TimeOffTypeIdentifier,
} from 'utils/constants/timeOffType';

export interface IOrganizationTimeOffSettingsDefinition {
  type: TimeOffTypeIdentifier;
  label: string;
  abbreviation: string;
}

export interface IOrganizationTimeOffSettings {
  active: TimeOffTypeIdentifier[];
  custom: IOrganizationTimeOffSettingsDefinition[];
}

// Constants
export const TIMEOFF_SETTINGS_MAX_CUSTOM_TYPES = 5;
export const TIMEOFF_SETTINGS_LABEL_MAX_LENGTH = 100;
export const TIMEOFF_SETTINGS_ABBREV_MAX_LENGTH = 3;
export const TIMEOFF_SETTINGS_ABBREV_REGEX = /^[A-Z0-9]+$/i;

const CUSTOM_TYPE_SET = new Set<TimeOffTypeIdentifier>(CUSTOM_TIMEOFF_TYPE_IDENTIFIERS);

// Helpers
function hasDuplicates<T>(arr: T[]): boolean {
  return new Set(arr).size !== arr.length;
}

export function isOrganizationTimeOffSettingsLabel(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const trimmed = v.trim();
  return trimmed.length > 0 && trimmed.length <= TIMEOFF_SETTINGS_LABEL_MAX_LENGTH;
}

export function isOrganizationTimeOffSettingsAbbreviation(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const trimmed = v.trim();
  const len = trimmed.length;
  return len > 0 && len <= TIMEOFF_SETTINGS_ABBREV_MAX_LENGTH && TIMEOFF_SETTINGS_ABBREV_REGEX.test(trimmed);
}

export function isOrganizationTimeOffSettingsDefinition(v: unknown): v is IOrganizationTimeOffSettingsDefinition {
  if (!v || typeof v !== 'object') return false;
  const def = v as Partial<IOrganizationTimeOffSettingsDefinition>;
  return (
    isCustomTimeOffTypeIdentifier(def.type) &&
    isOrganizationTimeOffSettingsLabel(def.label) &&
    isOrganizationTimeOffSettingsAbbreviation(def.abbreviation)
  );
}

// Composite validator
export function isOrganizationTimeOffSettings(obj: unknown): obj is IOrganizationTimeOffSettings {
  if (!obj || typeof obj !== 'object') return false;
  const o = obj as Partial<IOrganizationTimeOffSettings>;

  // Active validation
  if (!Array.isArray(o.active) || o.active.length === 0) return false;
  if (hasDuplicates(o.active)) return false;

  for (const id of o.active) {
    if (!Number.isInteger(id)) return false;
    if (!isTimeOffTypeIdentifier(id)) return false;
  }

  // Custom validation
  if (!Array.isArray(o.custom) || o.custom.length > TIMEOFF_SETTINGS_MAX_CUSTOM_TYPES) return false;

  const definedCustomTypes = new Set<number>();
  for (const def of o.custom) {
    if (!isOrganizationTimeOffSettingsDefinition(def)) return false;
    if (definedCustomTypes.has(def.type)) return false;
    definedCustomTypes.add(def.type);
  }

  // Ensure each active custom has a definition
  for (const id of o.active) {
    if (CUSTOM_TYPE_SET.has(id) && !definedCustomTypes.has(id)) return false;
  }

  return true;
}

/*
Example shape:
{
  "active": [10, 20, 30, 60, 80],
  "custom": [
    { "type": 60, "label": "Custom 1", "abbreviation": "C1" },
    { "type": 70, "label": "Custom 2", "abbreviation": "C2" },
    { "type": 80, "label": "Custom 3", "abbreviation": "C3" }
  ]
}
*/
